import { Description, Inventory, Visibility } from '@mui/icons-material';
import { AccordionDetails, Box, Chip, Grid, styled, Typography } from '@mui/material';

import InfoCard from './InfoCard';
import { EquipmentCard, NotesCard } from './styledComponents';

import {
  type DeliveryAndCollectionRegisterItem,
  type DeliveryAndCollectionItemState,
} from '@/graphql/generated/graphql';

const StyledAccordionDetails = styled(AccordionDetails)({
  maxHeight: '350px', // Set your desired fixed height here (in pixels)
  overflowY: 'auto', // Enable vertical scrolling
});

interface DeliveryCollectionCardBodyProps {
  deliveryItem: DeliveryAndCollectionRegisterItem;
  itemState: DeliveryAndCollectionItemState;
  uuid: string;
}

const DeliveryCollectionCardBody: React.FC<DeliveryCollectionCardBodyProps> = ({ deliveryItem, itemState, uuid }) => {
  return (
    <StyledAccordionDetails>
      <Grid container spacing={3} size={12}>
        {/* Left Column - Equipment Details */}
        <Grid
          size={{
            xs: 12,
            lg: 12,
          }}
        >
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Equipment Info */}
            <EquipmentCard>
              <Box display="flex" alignItems="center" mb={2}>
                <Inventory sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="h6" fontWeight={600}>
                  Details
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={4}>
                  <Typography variant="body2" color="text.secondary">
                    Category:
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {deliveryItem.companyResource?.resourceMeta?.resourceCategory?.name || '-'}
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="body2" color="text.secondary">
                    Short Code:
                  </Typography>
                  <Chip
                    label={deliveryItem.companyResource?.resourceMeta?.shortCode || '-'}
                    size="small"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      backgroundColor: '#e0e0e0',
                      mt: 0.5,
                    }}
                  />
                </Grid>
                <Grid size={4}>
                  <Typography variant="body2" color="text.secondary">
                    Quantity:
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {deliveryItem.quantity}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="body2" color="text.secondary">
                    Delivery ID:
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {uuid}
                  </Typography>
                </Grid>
              </Grid>
            </EquipmentCard>

            {/* People Involved */}
            <Grid container spacing={2}>
              <InfoCard variant="receiver" personName={itemState.receiverName} />
              <InfoCard variant="deliverer" personName={itemState.delivererName} />
              <InfoCard variant="supplier" personName={itemState.supplier} />
            </Grid>

            {/* Notes Section */}
            {itemState.notes && (
              <NotesCard>
                <Box display="flex" alignItems="flex-start">
                  <Description sx={{ mr: 1, color: '#f57f17', fontSize: 20, mt: 0.2 }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#f57f17', mb: 1 }}>
                      Special Notes
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#f57f17', lineHeight: 1.6 }}>
                      {itemState.notes}
                    </Typography>
                  </Box>
                </Box>
              </NotesCard>
            )}
          </Box>
        </Grid>

        {/* Right Column - Images */}
        <Grid
          size={{
            xs: 12,
            lg: 12,
          }}
        >
          <Box>
            <Box display="flex" alignItems="center" mb={2}>
              <Visibility sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="h6" fontWeight={600}>
                Images
              </Typography>
            </Box>
            <Grid container gap={2} flexDirection="row">
              {imageUrls.map((url, index) => (
                <Box
                  key={index}
                  component="img"
                  src={url}
                  alt={`Equipment image ${index + 1}`}
                  sx={{
                    width: '50%',
                    height: 128,
                    objectFit: 'cover',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 1,
                  }}
                />
              ))}
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </StyledAccordionDetails>
  );
};

export default DeliveryCollectionCardBody;

const imageUrls = [
  'https://images.unsplash.com/photo-1527864550417-7fd91fc51a46?w=400&h=300&fit=crop',
  'https://images.unsplash.com/photo-1518717758536-85ae29035b6d?w=400&h=300&fit=crop',
];
